/**
 * 62 achievements data
 * Headless data - no GUI
 */

import type { GameState } from '../state.js';

export type AchievementCategory = 'tap' | 'resource' | 'producer' | 'prestige' | 'time' | 'special' | 'ad' | 'combo' | 'conquest';

export interface AchievementDef {
  id: string;
  nameKey: string;
  icon: string;
  category: AchievementCategory;
  check: (state: GameState) => boolean;
  rewardDescKey: string;
}

// Tap achievements (8)
const TAP_ACHIEVEMENTS: AchievementDef[] = [
  { id: 'T01', nameKey: 'ach_t01', icon: '\u{1F446}', category: 'tap', check: s => s.tapCount >= 1, rewardDescKey: 'ach_t01_reward' },
  { id: 'T02', nameKey: 'ach_t02', icon: '\u{270B}', category: 'tap', check: s => s.tapCount >= 100, rewardDescKey: 'ach_t02_reward' },
  { id: 'T03', nameKey: 'ach_t03', icon: '\u{1F4A5}', category: 'tap', check: s => s.tapCount >= 1000, rewardDescKey: 'ach_t03_reward' },
  { id: 'T04', nameKey: 'ach_t04', icon: '\u{26A1}', category: 'tap', check: s => s.tapCount >= 10000, rewardDescKey: 'ach_t04_reward' },
  { id: 'T05', nameKey: 'ach_t05', icon: '\u{1F525}', category: 'tap', check: s => s.tapCount >= 100000, rewardDescKey: 'ach_t05_reward' },
  { id: 'T06', nameKey: 'ach_t06', icon: '\u{2604}', category: 'tap', check: s => s.tapCount >= 1000000, rewardDescKey: 'ach_t06_reward' },
  { id: 'T07', nameKey: 'ach_t07', icon: '\u{1F3AF}', category: 'tap', check: s => s.maxCombo >= 50, rewardDescKey: 'ach_t07_reward' },
  { id: 'T08', nameKey: 'ach_t08', icon: '\u{1F4AB}', category: 'tap', check: s => s.maxCombo >= 200, rewardDescKey: 'ach_t08_reward' },
];

// Resource achievements (10)
const RESOURCE_ACHIEVEMENTS: AchievementDef[] = [
  { id: 'R01', nameKey: 'ach_r01', icon: '\u{1F4B5}', category: 'resource', check: s => s.totalIpEarned >= 1000, rewardDescKey: 'ach_r01_reward' },
  { id: 'R02', nameKey: 'ach_r02', icon: '\u{1F4B0}', category: 'resource', check: s => s.totalIpEarned >= 10000, rewardDescKey: 'ach_r02_reward' },
  { id: 'R03', nameKey: 'ach_r03', icon: '\u{1F4B8}', category: 'resource', check: s => s.totalIpEarned >= 100000, rewardDescKey: 'ach_r03_reward' },
  { id: 'R04', nameKey: 'ach_r04', icon: '\u{1F4B3}', category: 'resource', check: s => s.totalIpEarned >= 1e6, rewardDescKey: 'ach_r04_reward' },
  { id: 'R05', nameKey: 'ach_r05', icon: '\u{1F48E}', category: 'resource', check: s => s.totalIpEarned >= 1e8, rewardDescKey: 'ach_r05_reward' },
  { id: 'R06', nameKey: 'ach_r06', icon: '\u{1F3E6}', category: 'resource', check: s => s.totalIpEarned >= 1e12, rewardDescKey: 'ach_r06_reward' },
  { id: 'R07', nameKey: 'ach_r07', icon: '\u{1F451}', category: 'resource', check: s => s.totalIpEarned >= 1e16, rewardDescKey: 'ach_r07_reward' },
  { id: 'R08', nameKey: 'ach_r08', icon: '\u{1F3C6}', category: 'resource', check: s => s.totalIpEarned >= 1e20, rewardDescKey: 'ach_r08_reward' },
  { id: 'R09', nameKey: 'ach_r09', icon: '\u{1FA90}', category: 'resource', check: s => s.totalIpEarned >= 1e24, rewardDescKey: 'ach_r09_reward' },
  { id: 'R10', nameKey: 'ach_r10', icon: '\u{1F320}', category: 'resource', check: s => s.totalIpEarned >= 1e32, rewardDescKey: 'ach_r10_reward' },
];

// Helper to count total producers
function totalProducerCount(s: GameState): number {
  return Object.values(s.producers).reduce((sum, p) => sum + p.count, 0);
}

function producerCount(s: GameState, id: string): number {
  return s.producers[id]?.count ?? 0;
}

// Producer achievements (12)
const PRODUCER_ACHIEVEMENTS: AchievementDef[] = [
  { id: 'G01', nameKey: 'ach_g01', icon: '\u{1F575}', category: 'producer', check: s => producerCount(s, 'informant') >= 1, rewardDescKey: 'ach_g01_reward' },
  { id: 'G02', nameKey: 'ach_g02', icon: '\u{1F465}', category: 'producer', check: s => producerCount(s, 'informant') >= 50, rewardDescKey: 'ach_g02_reward' },
  { id: 'G03', nameKey: 'ach_g03', icon: '\u{1F4BB}', category: 'producer', check: s => producerCount(s, 'hacker') >= 25, rewardDescKey: 'ach_g03_reward' },
  { id: 'G04', nameKey: 'ach_g04', icon: '\u{1F5A5}', category: 'producer', check: s => producerCount(s, 'hacker') >= 100, rewardDescKey: 'ach_g04_reward' },
  { id: 'G05', nameKey: 'ach_g05', icon: '\u{1F3AD}', category: 'producer', check: s => producerCount(s, 'disguiser') >= 50, rewardDescKey: 'ach_g05_reward' },
  { id: 'G06', nameKey: 'ach_g06', icon: '\u{1F9E0}', category: 'producer', check: s => producerCount(s, 'mindController') >= 50, rewardDescKey: 'ach_g06_reward' },
  { id: 'G07', nameKey: 'ach_g07', icon: '\u{1F52C}', category: 'producer', check: s => producerCount(s, 'secretLab') >= 25, rewardDescKey: 'ach_g07_reward' },
  { id: 'G08', nameKey: 'ach_g08', icon: '\u{1F4F0}', category: 'producer', check: s => producerCount(s, 'mediaCell') >= 25, rewardDescKey: 'ach_g08_reward' },
  { id: 'G09', nameKey: 'ach_g09', icon: '\u{1F3DB}', category: 'producer', check: s => producerCount(s, 'shadowGovt') >= 10, rewardDescKey: 'ach_g09_reward' },
  { id: 'G10', nameKey: 'ach_g10', icon: '\u{1F311}', category: 'producer', check: s => producerCount(s, 'deepBase') >= 10, rewardDescKey: 'ach_g10_reward' },
  { id: 'G11', nameKey: 'ach_g11', icon: '\u{1F6F0}', category: 'producer', check: s => producerCount(s, 'orbitalStation') >= 10, rewardDescKey: 'ach_g11_reward' },
  { id: 'G12', nameKey: 'ach_g12', icon: '\u{1F4E1}', category: 'producer', check: s => producerCount(s, 'galacticBeacon') >= 1, rewardDescKey: 'ach_g12_reward' },
];

// Prestige achievements (8)
const PRESTIGE_ACHIEVEMENTS: AchievementDef[] = [
  { id: 'P01', nameKey: 'ach_p01', icon: '\u{1F504}', category: 'prestige', check: s => s.prestigeCount >= 1, rewardDescKey: 'ach_p01_reward' },
  { id: 'P02', nameKey: 'ach_p02', icon: '\u{1F300}', category: 'prestige', check: s => s.prestigeCount >= 3, rewardDescKey: 'ach_p02_reward' },
  { id: 'P03', nameKey: 'ach_p03', icon: '\u{2728}', category: 'prestige', check: s => s.prestigeCount >= 5, rewardDescKey: 'ach_p03_reward' },
  { id: 'P04', nameKey: 'ach_p04', icon: '\u{1F31F}', category: 'prestige', check: s => s.prestigeCount >= 10, rewardDescKey: 'ach_p04_reward' },
  { id: 'P05', nameKey: 'ach_p05', icon: '\u{1F4AB}', category: 'prestige', check: s => s.prestigeCount >= 25, rewardDescKey: 'ach_p05_reward' },
  { id: 'P06', nameKey: 'ach_p06', icon: '\u{1F30C}', category: 'prestige', check: s => s.prestigeCount >= 50, rewardDescKey: 'ach_p06_reward' },
  { id: 'P07', nameKey: 'ach_p07', icon: '\u{1F52E}', category: 'prestige', check: s => s.prestige2Count >= 1, rewardDescKey: 'ach_p07_reward' },
  { id: 'P08', nameKey: 'ach_p08', icon: '\u{1F387}', category: 'prestige', check: s => s.prestige2Count >= 5, rewardDescKey: 'ach_p08_reward' },
];

// Time achievements (6)
const TIME_ACHIEVEMENTS: AchievementDef[] = [
  { id: 'H01', nameKey: 'ach_h01', icon: '\u{1F44B}', category: 'time', check: _s => true, rewardDescKey: 'ach_h01_reward' }, // first login
  { id: 'H02', nameKey: 'ach_h02', icon: '\u{23F1}', category: 'time', check: s => s.totalPlayTimeMs >= 3600000, rewardDescKey: 'ach_h02_reward' },
  { id: 'H03', nameKey: 'ach_h03', icon: '\u{23F0}', category: 'time', check: s => s.totalPlayTimeMs >= 43200000, rewardDescKey: 'ach_h03_reward' },
  { id: 'H04', nameKey: 'ach_h04', icon: '\u{1F553}', category: 'time', check: s => s.totalPlayTimeMs >= 86400000, rewardDescKey: 'ach_h04_reward' },
  { id: 'H05', nameKey: 'ach_h05', icon: '\u{1F4C5}', category: 'time', check: s => s.totalPlayTimeMs >= 259200000, rewardDescKey: 'ach_h05_reward' },
  { id: 'H06', nameKey: 'ach_h06', icon: '\u{231B}', category: 'time', check: s => s.totalPlayTimeMs >= 604800000, rewardDescKey: 'ach_h06_reward' },
];

// Special/hidden achievements (8)
const SPECIAL_ACHIEVEMENTS: AchievementDef[] = [
  { id: 'S01', nameKey: 'ach_s01', icon: '\u{1F50D}', category: 'special', check: _s => false, rewardDescKey: 'ach_s01_reward' }, // checked separately
  { id: 'S02', nameKey: 'ach_s02', icon: '\u{1F47D}', category: 'special', check: _s => false, rewardDescKey: 'ach_s02_reward' },
  { id: 'S03', nameKey: 'ach_s03', icon: '\u{1F6AB}', category: 'special', check: s => s.ip === 0 && s.totalIpEarned > 0, rewardDescKey: 'ach_s03_reward' },
  { id: 'S04', nameKey: 'ach_s04', icon: '\u{1F3C3}', category: 'special', check: _s => false, rewardDescKey: 'ach_s04_reward' }, // speed run - checked separately
  { id: 'S05', nameKey: 'ach_s05', icon: '\u{2B50}', category: 'special', check: s => s.goldenEventsTouched >= 100, rewardDescKey: 'ach_s05_reward' },
  { id: 'S06', nameKey: 'ach_s06', icon: '\u{1F319}', category: 'special', check: _s => false, rewardDescKey: 'ach_s06_reward' }, // night agent - checked separately
  { id: 'S07', nameKey: 'ach_s07', icon: '\u{1F4FA}', category: 'special', check: s => s.totalAdsWatched >= 50, rewardDescKey: 'ach_s07_reward' },
  { id: 'S08', nameKey: 'ach_s08', icon: '\u{1F3C5}', category: 'special', check: s => {
    const count = Object.values(s.achievements).filter(a => a.unlocked).length;
    return count >= 50;
  }, rewardDescKey: 'ach_s08_reward' },
];

// Ad/docs achievements (5)
const AD_ACHIEVEMENTS: AchievementDef[] = [
  { id: 'A01', nameKey: 'ach_a01', icon: '\u{1F4F9}', category: 'ad', check: s => s.totalAdsWatched >= 1, rewardDescKey: 'ach_a01_reward' },
  { id: 'A02', nameKey: 'ach_a02', icon: '\u{1F39E}', category: 'ad', check: s => s.totalAdsWatched >= 10, rewardDescKey: 'ach_a02_reward' },
  { id: 'A03', nameKey: 'ach_a03', icon: '\u{1F4FC}', category: 'ad', check: s => s.totalAdsWatched >= 50, rewardDescKey: 'ach_a03_reward' },
  { id: 'A04', nameKey: 'ach_a04', icon: '\u{1F4DC}', category: 'ad', check: s => s.totalDocsUsed >= 10, rewardDescKey: 'ach_a04_reward' },
  { id: 'A05', nameKey: 'ach_a05', icon: '\u{1F4DA}', category: 'ad', check: s => s.totalDocsUsed >= 50, rewardDescKey: 'ach_a05_reward' },
];

// Combo/event achievements (5)
const COMBO_ACHIEVEMENTS: AchievementDef[] = [
  { id: 'C01', nameKey: 'ach_c01', icon: '\u{1F525}', category: 'combo', check: s => s.maxCombo >= 10, rewardDescKey: 'ach_c01_reward' },
  { id: 'C02', nameKey: 'ach_c02', icon: '\u{1F4A2}', category: 'combo', check: s => s.maxCombo >= 30, rewardDescKey: 'ach_c02_reward' },
  { id: 'C03', nameKey: 'ach_c03', icon: '\u{1F31F}', category: 'combo', check: s => s.goldenEventsTouched >= 10, rewardDescKey: 'ach_c03_reward' },
  { id: 'C04', nameKey: 'ach_c04', icon: '\u{2747}', category: 'combo', check: s => s.goldenEventsTouched >= 50, rewardDescKey: 'ach_c04_reward' },
  { id: 'C05', nameKey: 'ach_c05', icon: '\u{1F4CB}', category: 'combo', check: _s => false, rewardDescKey: 'ach_c05_reward' }, // daily mission
];

// Conquest achievements (6)
const CONQUEST_ACHIEVEMENTS: AchievementDef[] = [
  { id: 'Q01', nameKey: 'ach_q01', icon: '\u{1F6A9}', category: 'conquest', check: s => getConqueredCountForAch(s) >= 1, rewardDescKey: 'ach_q01_reward' },
  { id: 'Q02', nameKey: 'ach_q02', icon: '\u{1F3F4}', category: 'conquest', check: s => getConqueredCountForAch(s) >= 3, rewardDescKey: 'ach_q02_reward' },
  { id: 'Q03', nameKey: 'ach_q03', icon: '\u{1F30D}', category: 'conquest', check: s => getConqueredCountForAch(s) >= 5, rewardDescKey: 'ach_q03_reward' },
  { id: 'Q04', nameKey: 'ach_q04', icon: '\u{1F30E}', category: 'conquest', check: s => getConqueredCountForAch(s) >= 7, rewardDescKey: 'ach_q04_reward' },
  { id: 'Q05', nameKey: 'ach_q05', icon: '\u{1F30F}', category: 'conquest', check: s => getConqueredCountForAch(s) >= 10, rewardDescKey: 'ach_q05_reward' },
  { id: 'Q06', nameKey: 'ach_q06', icon: '\u{1F3D7}', category: 'conquest', check: s => getTotalFacilityLevels(s) >= 30, rewardDescKey: 'ach_q06_reward' },
];

function getConqueredCountForAch(s: GameState): number {
  if (!s.conquest) return 0;
  return Object.values(s.conquest.countries).filter(c => c.conquered).length;
}

function getTotalFacilityLevels(s: GameState): number {
  if (!s.conquest) return 0;
  let total = 0;
  for (const cs of Object.values(s.conquest.countries)) {
    for (const lv of cs.facilities) total += lv;
  }
  return total;
}

export const ALL_ACHIEVEMENTS: AchievementDef[] = [
  ...TAP_ACHIEVEMENTS,
  ...RESOURCE_ACHIEVEMENTS,
  ...PRODUCER_ACHIEVEMENTS,
  ...PRESTIGE_ACHIEVEMENTS,
  ...TIME_ACHIEVEMENTS,
  ...SPECIAL_ACHIEVEMENTS,
  ...AD_ACHIEVEMENTS,
  ...COMBO_ACHIEVEMENTS,
  ...CONQUEST_ACHIEVEMENTS,
];

export { totalProducerCount };

/** Check all achievements and return newly unlocked IDs */
export function checkAchievements(state: GameState): string[] {
  const newlyUnlocked: string[] = [];
  for (const ach of ALL_ACHIEVEMENTS) {
    if (state.achievements[ach.id]?.unlocked) continue;
    if (ach.check(state)) {
      newlyUnlocked.push(ach.id);
    }
  }
  return newlyUnlocked;
}
